import axios from 'axios';

// Account-recovery microservice client.
//
// Talks to the standalone account-recovery service, NOT the main platform API.
// The base is configurable via REACT_APP_RECOVERY_URL so the same build can be
// pointed at a local service in dev and at the nginx-routed path in prod. Default
// is a relative '/recovery' path which nginx routes to the service at deploy time.
//
// This client is deliberately independent of helpers/api_helper.js: the recovery
// flow is unauthenticated (no session cookie, no refresh interceptor) and must
// not be coupled to the cabinet's auth lifecycle.
export const RECOVERY_URL = process.env.REACT_APP_RECOVERY_URL || '/recovery';

const recoveryApi = axios.create({
  baseURL: RECOVERY_URL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

class RecoveryService {
  // GET /account-recovery/config — public, unauthenticated. Returns the captcha
  // policy the frontend must honour:
  //   { captcha_enabled, captcha_provider, captcha_sitekey }
  // The sitekey is a PUBLIC key (safe to ship to the browser). The frontend
  // falls back to REACT_APP_TURNSTILE_SITEKEY if the service is unreachable.
  async getConfig() {
    const response = await recoveryApi.get('/account-recovery/config');
    return response.data;
  }

  // POST /account-recovery — intake. Returns { case_ref } with an IDENTICAL shape
  // whether or not an account matched (anti-enumeration is enforced server-side).
  // `caseRef` (optional) carries an opaque reference back from a rejection email
  // deep-link so the SAME case is re-opened instead of a new one being created.
  // `captchaToken` (optional) is the Cloudflare Turnstile token; it is sent in the
  // X-Captcha-Token header (the service also accepts a body field, but the header
  // keeps the token out of any body-logging path).
  async intake({ claimedOriginalEmail, newEmail, note, caseRef, captchaToken }) {
    const body = {
      claimed_original_email: claimedOriginalEmail,
      new_email: newEmail,
    };
    if (note) body.note = note;
    if (caseRef) body.case_ref = caseRef;

    const config = captchaToken ? { headers: { 'X-Captcha-Token': captchaToken } } : undefined;
    const response = await recoveryApi.post('/account-recovery', body, config);
    return response.data;
  }

  // POST /account-recovery/{case_ref}/verify-email — verify the code sent to the
  // new email. Returns { status: "NEW_EMAIL_VERIFIED", verification_url? } on 200.
  // A generic 400 means wrong/expired/exhausted code OR unknown case_ref — the
  // service never distinguishes which (anti-enumeration), so neither do we.
  async verifyEmail({ caseRef, code }) {
    const response = await recoveryApi.post(
      `/account-recovery/${encodeURIComponent(caseRef)}/verify-email`,
      { code },
    );
    return response.data;
  }

  // POST /account-recovery/{case_ref}/check-identity — action-driven liveness
  // check. Called when the user returns from the AMLBot check (button / tab-focus
  // / short poll). Returns a COARSE { status } — "pending" | "in_review" |
  // "failed" — never the internal lifecycle (anti-enumeration). Always resolves to
  // a status; on a transient error the caller treats it as "pending" and retries.
  async checkIdentity(caseRef) {
    const response = await recoveryApi.post(
      `/account-recovery/${encodeURIComponent(caseRef)}/check-identity`,
    );
    return response.data;
  }

  // POST /account-recovery/{case_ref}/set-password — final step (Option B). The
  // client arrives here via a one-time link the operator sends after approval:
  //   {RECOVERY_SETPW_URL}?token=...&case_ref=...
  // `token` is the single-use grant; `new_password` is the password to install.
  // Status handling (mapped to copy in the page, never enumerating):
  //   200 → success           400 → link invalid/expired
  //   409 → email already in use   423 → too many attempts / locked
  //   503 / network → transient, retry-safe
  async setPassword(caseRef, token, newPassword) {
    const response = await recoveryApi.post(
      `/account-recovery/${encodeURIComponent(caseRef)}/set-password`,
      { token, new_password: newPassword },
    );
    return response.data;
  }
}

export default new RecoveryService();
